import url from 'url';


/**
 * Query params preserved across navigation within our experience.
 *  Shared by the client-side history override (HistoryStore) and the SSR
 *  redirect in renderPageMiddleware so both keep the same set.
 */
export const PRESERVED_QUERY_PARAMS = [
    'make',
    'partner',
    'partnerVehicleMatch',
    'appTheme',
    'dealership',
    'utm_source',
    'utm_medium',
    'utm_campaign',
    'external_dealership_id',
    // Id of the assigned dealer link
    'a',
    // google tag id from partner
    'partnerGId',
    'flow',
    'isFlex',
    'inStore',
    'instore',
];

/**
 * Builds a same-origin redirect path, carrying over the PRESERVED_QUERY_PARAMS
 *  that the redirect target doesn't already specify.
 *
 * Only relative targets are trusted - our redirects are always in-app, so an
 *  absolute or protocol-relative target is replaced with "/".
 *
 * @param redirectUrl {string} - redirect target (e.g. "/paperwork/some-uuid")
 * @param query {object} - incoming request query params
 * @param baseUrl {string} - origin used to resolve the relative target
 * @returns {string} - path + search + hash (e.g. "/paperwork/some-uuid?appTheme=abc")
 */
export function buildPreservedRedirectPath(redirectUrl, query = {}, baseUrl) {
    const isRelativePath = redirectUrl.startsWith('/') && !redirectUrl.startsWith('//');
    const targetUrl = new URL(isRelativePath ? redirectUrl : '/', baseUrl);

    PRESERVED_QUERY_PARAMS.forEach(param => {
        // The query parser returns an array when a param repeats; keep the first
        // value, matching how ThemeStore normalizes duplicate appTheme params
        const rawValue = query[param];
        const value = Array.isArray(rawValue) ? rawValue[0] : rawValue;
        if (typeof value === 'string' && !targetUrl.searchParams.has(param)) {
            targetUrl.searchParams.set(param, value);
        }
    });

    return targetUrl.pathname + targetUrl.search + targetUrl.hash;
}

export function stripTrailingSlash(uri) {
    if (uri.substr(-1) === '/') {
        return uri.slice(0, -1);
    }
    return uri;
}

export function stripLeadingSlash(uri) {
    if (uri[0] === '/') {
        return uri.slice(1);
    }
    return uri;
}

export function stripLeadingAndTrailingSlash(uri) {
    return stripLeadingSlash(stripTrailingSlash(uri));
}

/**
 * Returns the uri without the protocol and domain
 *
 * @param uri {string}
 * @returns {string}
 */
export function stripQueryParams(uri) {
    const index = uri.indexOf('?');
    if (index >= 0) {
        return uri.slice(0, index);
    }
    return uri;
}

/**
 * Returns the uri without the protocol and domain
 *  e.g. "http://www.rideoctane.com/a/b/c" => "/a/b/c"
 *
 * @param uri {string}
 * @returns {string}
 */
export function stripPortAndDomain(uri) {
    const {path} = url.parse(uri);
    return path || "";
}

/**
 * Returns the slugs in the uri
 *  e.g. "http://www.rideoctane.com/a/b/c" => ['a', 'b', 'c']
 *
 * @param uri {string}
 * @returns {array}
 */
export function getPathSegments(uri) {
    uri = stripQueryParams(uri);
    uri = stripPortAndDomain(uri);
    uri = stripLeadingAndTrailingSlash(uri);
    return uri ? uri.split('/') : [];
}

/**
 * Returns a specified uri segment
 *
 * @param uri {string}
 * @param at {string|number} - "first", "last" or an index number
 * @returns {string|null}
 */
export function getPathSegment(uri, at = "first") {
    const slugs = getPathSegments(uri);

    switch (at) {
        case "first":
            return slugs.shift() || null;
        case "last":
            return slugs.pop() || null;
        default:
            return slugs[at] || null;
    }
}